import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaMoneyBillWave, 
  FaShieldAlt, 
  FaBoxOpen, 
  FaBullhorn, 
  FaGraduationCap, 
  FaHandshake,
  FaBuilding,
  FaStore,
  FaTruck,
  FaRupeeSign,
  FaQuoteLeft,
  FaWhatsapp,
  FaPhoneAlt
} from 'react-icons/fa';
import './Distributor.css';

const Distributor = () => {
  const benefits = [
    {
      icon: <FaMoneyBillWave />,
      title: 'Attractive Margins',
      description: 'Enjoy competitive profit margins on all products with volume-based incentives'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Territory Protection',
      description: 'Exclusive rights to your designated territory for focused business growth'
    },
    {
      icon: <FaBoxOpen />,
      title: 'Reliable Supply',
      description: 'Consistent product availability with efficient logistics support'
    },
    {
      icon: <FaBullhorn />,
      title: 'Marketing Support',
      description: 'Point-of-sale materials, banners, and promotional assistance'
    },
    {
      icon: <FaGraduationCap />,
      title: 'Training',
      description: 'Product knowledge and sales training for you and your team'
    },
    {
      icon: <FaHandshake />,
      title: 'Dedicated Support',
      description: 'Personal relationship manager for all your business needs'
    }
  ];

  const requirements = [
    {
      icon: <FaBuilding />,
      title: 'Business Setup',
      points: [
        'Registered business entity (Proprietorship/Partnership/Company)',
        'GST registration',
        'Valid trade license',
        'Established retail network'
      ]
    },
    {
      icon: <FaStore />,
      title: 'Infrastructure',
      points: [
        'Minimum 500 sq.ft. storage space',
        'Proper shelving and storage conditions',
        'Loading/unloading facilities',
        'Basic office setup'
      ]
    },
    {
      icon: <FaTruck />,
      title: 'Distribution',
      points: [
        'Own or leased delivery vehicles',
        'Coverage capability in assigned territory',
        'Delivery staff for retail distribution',
        'Basic inventory management'
      ]
    },
    {
      icon: <FaMoneyBillWave />,
      title: 'Investment',
      points: [
        'Security deposit as per territory',
        'Working capital for stock',
        'Vehicle and staff expenses',
        'Minimum monthly commitment'
      ]
    }
  ];

  return (
    <div className="distributor-page">
      {/* Page Hero */}
      <section className="dist-hero">
        <div className="hero-bg">
          <img src="/About Page images/distributor-truck.png" alt="Distributor" />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="dist-hero-content">
            <span className="hero-badge"><FaHandshake /> Partnership Opportunity</span>
            <h1>Become a Distributor</h1>
            <p>Join our growing network of successful business partners</p>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Become Distributor</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="why-partner section">
        <div className="container">
          <div className="section-header">
            <h2>Why Partner with Shikhar Dhara?</h2>
            <p>Join a brand committed to quality, growth, and mutual success</p>
          </div>
          
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products to Distribute */}
      <section className="products-distribute section bg-light">
        <div className="container">
          <div className="section-header">
            <h2>Products You'll Distribute</h2>
            <p>Complete range of premium beverages for all customer segments</p>
          </div>
          
          <div className="product-range-grid">
            <div className="range-card water">
              <div className="range-image">
                <img src="/Home Page images/water-bottles-range.jpg" alt="Water Range" />
              </div>
              <div className="range-content">
                <span className="range-badge">💧 Drinking Water</span>
                <h3>Packaged Drinking Water</h3>
                <ul>
                  <li>250ml Bottles</li>
                  <li>500ml Bottles</li>
                  <li>1 Litre Bottles</li>
                  <li>20 Litre Jars</li>
                </ul>
                <Link to="/water-bottles" className="btn btn-primary">
                  View Products
                </Link>
              </div>
            </div>
            <div className="range-card juice">
              <div className="range-image">
                <img src="/Home Page images/litchi-juice-range.jpg" alt="Juice Range" />
              </div>
              <div className="range-content">
                <span className="range-badge green">🍈 Fruit Juice</span>
                <h3>Litchi Fruit Juice</h3>
                <ul>
                  <li>200ml Tetra Pack</li>
                  <li>500ml PET Bottle</li>
                  <li>1 Litre PET Bottle</li>
                </ul>
                <Link to="/litchi-juice" className="btn btn-green">
                  View Products
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="requirements-section section">
        <div className="container">
          <div className="section-header">
            <h2>Partnership Requirements</h2>
            <p>What you need to become a Shikhar Dhara distributor</p>
          </div>
          
          <div className="requirements-grid">
            {requirements.map((req, index) => (
              <div key={index} className="requirement-card">
                <div className="req-icon">{req.icon}</div>
                <h4>{req.title}</h4>
                <ul>
                  {req.points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment & Returns */}
      <section className="investment-section section bg-light">
        <div className="container">
          <div className="investment-grid">
            <div className="investment-content">
              <span className="section-label">Business Opportunity</span>
              <h2>Investment & Expected Returns</h2>
              <p>
                Becoming a Shikhar Dhara distributor is a profitable business 
                opportunity with attractive margins and growth potential.
              </p>
              
              <div className="investment-details">
                <div className="inv-item">
                  <span className="inv-label">Initial Investment</span>
                  <span className="inv-value"><FaRupeeSign /> 2-5 Lakhs*</span>
                </div>
                <div className="inv-item">
                  <span className="inv-label">Expected Monthly ROI</span>
                  <span className="inv-value">15-25%</span>
                </div>
                <div className="inv-item">
                  <span className="inv-label">Breakeven Period</span>
                  <span className="inv-value">3-6 Months</span>
                </div>
              </div>
              
              <p className="inv-note">*Investment varies based on territory and scope</p>
            </div>
            <div className="investment-image">
              <img src="/About Page images/distributor-success.jpg" alt="Successful Distributor" />
            </div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="application-process section">
        <div className="container">
          <div className="section-header">
            <h2>How to Apply</h2>
            <p>Simple steps to become a Shikhar Dhara distributor</p>
          </div>
          
          <div className="process-steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h4>Submit Interest</h4>
              <p>Contact us via WhatsApp or phone to express your interest</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h4>Discussion</h4>
              <p>Our team will discuss territory, investment, and terms</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h4>Documentation</h4>
              <p>Submit required documents and complete formalities</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step-card">
              <div className="step-number">4</div>
              <h4>Onboarding</h4>
              <p>Training, initial stock, and begin distribution</p>
            </div>
          </div>
        </div>
      </section>

      {/* Territory Map */}
      <section className="territory-section section bg-light">
        <div className="container">
          <div className="territory-content">
            <div className="territory-text">
              <h2>Territories Available</h2>
              <p>
                We are actively looking for distributors in various regions of 
                Uttar Pradesh and neighboring states. Check if your area is 
                available for distribution.
              </p>
              <div className="territory-list">
                <div className="territory-item available">
                  <span className="status">●</span>
                  <span>Western UP Districts</span>
                </div>
                <div className="territory-item available">
                  <span className="status">●</span>
                  <span>Rohilkhand Region</span>
                </div>
                <div className="territory-item available">
                  <span className="status">●</span>
                  <span>NCR Region</span>
                </div>
                <div className="territory-item available">
                  <span className="status">●</span>
                  <span>Uttarakhand</span>
                </div>
                <div className="territory-item limited">
                  <span className="status">●</span>
                  <span>Central UP (Limited)</span>
                </div>
              </div>
              <p className="territory-note">Contact us to check availability in your specific area</p>
            </div>
            <div className="territory-cta">
              <div className="cta-card">
                <h3>Ready to Join?</h3>
                <p>Start your journey with Shikhar Dhara today</p>
                <a 
                  href="https://wa.me/918077894559?text=Hello, I am interested in becoming a Shikhar Dhara distributor. Please share the details."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-whatsapp btn-lg"
                >
                  <FaWhatsapp /> WhatsApp Now
                </a>
                <div style={{display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center'}}>
                  <a href="tel:+918077894559" className="btn btn-secondary btn-lg">
                    <FaPhoneAlt /> Call: +91 8077894559
                  </a>
                  <a href="tel:+917983864997" className="btn btn-secondary btn-lg">
                    <FaPhoneAlt /> Call: +91 79838 64997
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="success-stories section">
        <div className="container">
          <div className="section-header">
            <h2>Partner Success Stories</h2>
            <p>Hear from our successful distribution partners</p>
          </div>
          
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="quote-icon"><FaQuoteLeft /></div>
              <p className="testimonial-text">
                "Partnering with Shikhar Dhara has been a game-changer for my 
                business. The product quality ensures customer loyalty and 
                the margins are excellent."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <h5>Rajesh Kumar</h5>
                  <span>Distributor, Bareilly</span>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="quote-icon"><FaQuoteLeft /></div>
              <p className="testimonial-text">
                "The company support is outstanding. From training to marketing 
                materials, everything is provided. My sales have grown 40% in 
                the first year."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <h5>Priya Sharma</h5>
                  <span>Distributor, Rampur</span>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="quote-icon"><FaQuoteLeft /></div>
              <p className="testimonial-text">
                "Reliable supply and great product demand made my distribution 
                business profitable within 4 months. Highly recommend partnering 
                with Shikhar Dhara."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <h5>Amit Singh</h5>
                  <span>Distributor, Moradabad</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="distributor-faq section bg-light">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Common questions about distributorship</p>
          </div>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h4>What is the minimum investment required?</h4>
              <p>The investment varies from <FaRupeeSign /> 2-5 lakhs depending on territory size and scope. This includes security deposit and initial stock.</p>
            </div>
            <div className="faq-item">
              <h4>How is the territory assigned?</h4>
              <p>Territories are assigned based on population, market potential, and your distribution capacity. We ensure fair coverage for each partner.</p>
            </div>
            <div className="faq-item">
              <h4>What support does the company provide?</h4>
              <p>We provide marketing materials, training, promotional support, and a dedicated relationship manager for all your needs.</p>
            </div>
            <div className="faq-item">
              <h4>What are the payment terms?</h4>
              <p>We work on advance payment basis for initial orders. Credit terms are available after establishing a successful track record.</p>
            </div>
            <div className="faq-item">
              <h4>Can I distribute both water and juice?</h4>
              <p>Yes, distributors can handle both product lines. Combined distribution often leads to better margins and customer coverage.</p>
            </div>
            <div className="faq-item">
              <h4>Is there any minimum order quantity?</h4>
              <p>Minimum order quantities vary by product and territory. Our team will discuss specific requirements during onboarding.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="distributor-cta">
        <div className="container">
          <div className="dist-cta-content">
            <h2>Start Your Success Story Today</h2>
            <p>Join the Shikhar Dhara family and grow your business with us</p>
            <div className="dist-cta-buttons">
              <a 
                href="https://wa.me/918077894559?text=Hello, I want to become a Shikhar Dhara distributor."
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp btn-lg"
              >
                <FaWhatsapp /> WhatsApp to Apply
              </a>
              <Link to="/contact" className="btn btn-white btn-lg">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Distributor;
